import { useState, useEffect, useCallback } from 'react';
import {
  getKidsChildren, upsertKidsChild, deleteKidsChild,
  upsertKidsGroup, deleteKidsGroup, syncKidsChildrenNow,
} from '../../utils/api';
import { BrandSelect, ColorSwatch, GroupPill, NewTag, AllergyText, usePager, textOn, KIDS_TEAL, programLabel, formatPhone, cleanAllergy, sortKidsGroups, MANUAL_ORDER_BASE } from './kidsUi';
import { downloadCsv } from '../../utils/csv';

// Small Groups — the child-management heart of the Kids staff view.
// Children auto-sync from Brushfire (grouped by the meeting rules) and can be
// moved between groups manually (a manual move is never overwritten by sync).
// Kids added in the last 24 hours get a teal "New" tag so leaders spot them.

const PROGRAMS = ['Elementary', '5-6', '3-4'];

export default function KidsSmallGroups({ team, showToast, readOnly }) {
  const [children, setChildren] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lastSync, setLastSync] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [programFilter, setProgramFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const [editKid, setEditKid] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getKidsChildren()
      .then(d => {
        setChildren(d?.children || []);
        setGroups(d?.groups || []);
        setLastSync(d?.lastSync || '');
      })
      .catch(() => showToast('Could not load kids — check the Apps Script deploy.'))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  async function handleSync() {
    setSyncing(true);
    try {
      const r = await syncKidsChildrenNow();
      if (r?.ok) showToast(`Synced — ${r.added || 0} added, ${r.updated || 0} updated.`);
      else showToast(r?.error || 'Sync failed.');
      load();
    } catch (e) {
      showToast('Sync failed — check the Apps Script logs.');
    } finally {
      setSyncing(false);
    }
  }

  async function moveChild(child, groupId) {
    const prev = children;
    const update = { id: child.id, groupId, groupManual: 'true' };
    // Moving to a group in another program (e.g. a 6-year-old from 5s & 6s
    // into an Elementary group) moves the kid's program too, and locks it so
    // the sync's age-based grouping never pulls them back.
    const target = groups.find(g => g.id === groupId);
    if (target?.program && child.program && target.program !== child.program) {
      update.program = target.program;
      const locked = new Set((child.manualFields || '').split(',').map(s => s.trim()).filter(Boolean));
      locked.add('program');
      update.manualFields = Array.from(locked).join(',');
    }
    setChildren(list => list.map(c => c.id === child.id ? { ...c, ...update } : c));
    try {
      await upsertKidsChild(update);
      showToast(update.program ? `${child.firstName} moved to ${target.program === 'Elementary' ? 'Elementary' : target.program} — program updated.` : `${child.firstName} moved.`);
    } catch (e) {
      setChildren(prev);
      showToast('Move failed — try again.');
    }
  }

  const groupById = {};
  groups.forEach(g => { groupById[g.id] = g; });

  const active = children.filter(c => c.status !== 'Cancelled');
  const filtered = active.filter(c => {
    if (programFilter && c.program !== programFilter) return false;
    if (groupFilter === '__none') { if (c.groupId) return false; }
    else if (groupFilter && c.groupId !== groupFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      const name = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });
  const { slice, controls } = usePager(filtered, 25);

  const visibleGroups = programFilter ? groups.filter(g => g.program === programFilter) : groups;
  const unassigned = active.filter(c => !c.groupId && (!programFilter || c.program === programFilter)).length;

  return (
    <>
      <div style={{ padding: '24px 40px 16px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 16 }}>
          <div>
            <div className="page-title">Small Groups</div>
            <div className="page-sub">
              {active.length} kids · {groups.length} groups
              {lastSync ? ` · last sync ${new Date(lastSync).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {!readOnly && (
              <button
                className="btn btn-sm"
                title="Download every kid + their small group as a CSV (for the PCO nametag import)"
                onClick={() => {
                  const progLabel = p => p === '3-4' ? '3s & 4s' : p === '5-6' ? '5s & 6s' : (p || '');
                  const headers = ['First Name', 'Last Name', 'Grade', 'Gender', 'Program', 'Small Group', 'Allergies', 'Parent/Guardian', 'Parent Contact', 'Brushfire ID'];
                  const rows = active.map(c => [
                    c.firstName || '', c.lastName || '', c.grade || '', c.gender || '',
                    progLabel(c.program),
                    (c.groupId && groupById[c.groupId]) ? groupById[c.groupId].name : '',
                    cleanAllergy(c.displayAllergies),
                    c.parentName || '', c.parentContact || '', c.brushfireId || '',
                  ]);
                  downloadCsv('kids-small-groups.csv', headers, rows);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 5, verticalAlign: -2 }}>
                  <path d="M7 1v8M4 6l3 3 3-3M2 12.5h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Export CSV
              </button>
            )}
            {!readOnly && <button className="btn btn-sm" onClick={handleSync} disabled={syncing}>{syncing ? 'Syncing…' : 'Sync from Brushfire'}</button>}
            {!readOnly && <button className="btn btn-sm" onClick={() => setGroupEditOpen(true)}>Manage Groups</button>}
            {!readOnly && <button className="btn btn-dark" onClick={() => setAddOpen(true)}>+ Add Kid</button>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginRight: 2 }}>Program</span>
          <button className={`stat-chip${!programFilter ? ' active-chip' : ''}`} onClick={() => { setProgramFilter(''); setGroupFilter(''); }}>All</button>
          {PROGRAMS.map(p => (
            <button key={p} className={`stat-chip${programFilter === p ? ' active-chip' : ''}`} onClick={() => { setProgramFilter(programFilter === p ? '' : p); setGroupFilter(''); }}>
              <span className="stat-chip-num">{active.filter(c => c.program === p).length}</span> {p}
            </button>
          ))}
          {unassigned > 0 && (
            <button
              className="pill pill-amber"
              onClick={() => setGroupFilter(groupFilter === '__none' ? '' : '__none')}
              title="Show kids with no group"
              style={{ marginLeft: 6, border: groupFilter === '__none' ? '2px solid #7a4f0a' : 'none', cursor: 'pointer' }}
            >
              {unassigned} unassigned
            </button>
          )}
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: 24 }}>
        {/* Group cards — white with a colored header, per the approved design */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 28 }}>
          {visibleGroups.map(g => {
            const count = active.filter(c => c.groupId === g.id).length;
            const bg = g.color || KIDS_TEAL;
            const selected = groupFilter === g.id;
            return (
              <div
                key={g.id}
                onClick={() => setGroupFilter(selected ? '' : g.id)}
                style={{ border: `1px solid ${selected ? bg : 'var(--border)'}`, outline: selected ? `2px solid ${bg}` : 'none', outlineOffset: -2, borderRadius: 8, overflow: 'hidden', background: '#fff', cursor: 'pointer' }}
              >
                <div style={{ background: bg, color: textOn(bg), fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', padding: '7px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{g.name}</span>
                  <span style={{ opacity: 0.85 }}>{count}</span>
                </div>
                <div style={{ padding: '8px 12px', fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
                  {programLabel(g.program)}
                </div>
              </div>
            );
          })}
          {visibleGroups.length === 0 && !loading && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', gridColumn: '1/-1' }}>
              No groups yet — sync from Brushfire to auto-create them, or add one under Manage Groups.
            </div>
          )}
        </div>

        {/* Kids table */}
        <div style={{ marginBottom: 12, maxWidth: 360 }}>
          <input className="search-bar" placeholder="Search kids by name…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="panel">
          {loading ? (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>Loading kids…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
              No kids match. Run a Brushfire sync or add one manually.
            </div>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Grade / Age</th><th>Program</th><th>Group</th><th>Allergies</th><th>Parent</th><th></th></tr>
                </thead>
                <tbody>
                  {slice.map(c => (
                    <tr key={c.id} style={{ cursor: 'default' }}>
                      <td>
                        <span className="td-name">{c.firstName} {c.lastName}</span>
                        {c.isNew && <NewTag />}
                        {c.source === 'Manual' && <span className="pill pill-gray" style={{ fontSize: 9, marginLeft: 6 }}>Manual</span>}
                      </td>
                      <td>{c.grade ? `Grade ${c.grade}` : c.age ? `Age ${c.age}` : '—'}{c.gender ? ` · ${c.gender}` : ''}</td>
                      <td>{c.program || '—'}</td>
                      <td>
                        {readOnly ? (
                          c.groupId && groupById[c.groupId] ? <GroupPill name={groupById[c.groupId].name} color={groupById[c.groupId].color} /> : <span style={{ color: 'var(--subtle)' }}>—</span>
                        ) : (
                          <BrandSelect
                            size="sm"
                            value={c.groupId || ''}
                            // Same-program groups first, then every other group
                            // (labeled with its program) — a kid on the 5s/6s
                            // boundary can be moved straight into Elementary,
                            // which updates + locks their program too.
                            options={['',
                              ...groups.filter(g => !c.program || !g.program || g.program === c.program).map(g => g.id),
                              ...groups.filter(g => c.program && g.program && g.program !== c.program).map(g => g.id),
                            ]}
                            labels={{ '': '— no group —', ...Object.fromEntries(groups.map(g => [g.id,
                              (!c.program || !g.program || g.program === c.program) ? g.name : `${g.name} · ${programLabel(g.program)}`,
                            ])) }}
                            onChange={gid => moveChild(c, gid)}
                          />
                        )}
                      </td>
                      <td><AllergyText>{cleanAllergy(c.displayAllergies)}</AllergyText></td>
                      <td style={{ fontSize: 12 }}>{c.parentName || '—'}{c.parentContact ? ` · ${formatPhone(c.parentContact)}` : ''}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {!readOnly && (
                          <button className="btn btn-xs" onClick={() => setEditKid(c)}>Edit</button>
                        )}
                        {!readOnly && c.source === 'Manual' && (
                          <button className="btn-remove" title="Remove (manual add)" style={{ marginLeft: 6 }} onClick={async () => {
                            await deleteKidsChild(c.id).catch(() => {});
                            setChildren(list => list.filter(x => x.id !== c.id));
                            showToast('Removed.');
                          }}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {controls}
            </>
          )}
        </div>
      </div>

      {addOpen && (
        <AddKidModal
          groups={groups}
          onClose={() => setAddOpen(false)}
          onSaved={(child) => { setChildren(list => [...list, { ...child, isNew: true, displayAllergies: child.allergyTags || child.allergyRaw || '' }]); setAddOpen(false); showToast('Kid added.'); }}
          showToast={showToast}
        />
      )}

      {groupEditOpen && (
        <ManageGroupsModal
          groups={groups}
          onClose={() => setGroupEditOpen(false)}
          onChanged={load}
          showToast={showToast}
        />
      )}

      {editKid && (
        <EditKidModal
          child={editKid}
          groups={groups}
          onClose={() => setEditKid(null)}
          onSaved={(updated) => {
            setChildren(list => list.map(c => c.id === updated.id ? { ...c, ...updated } : c));
            setEditKid(null);
            showToast('Saved — your edits are protected from Brushfire syncs.');
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

// Full edit for any kid — parents mistype things, so EVERYTHING is fixable in
// the app and the app wins: every field edited here is added to manualFields,
// which the Brushfire sync refuses to overwrite. Allergy edits go through
// allergyOverride (registration's value stays visible for reference).
const EDITABLE_REG_FIELDS = ['firstName', 'lastName', 'grade', 'gender', 'program', 'parentName', 'parentContact'];

function EditKidModal({ child, groups, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    firstName: child.firstName || '',
    lastName: child.lastName || '',
    grade: child.grade || '',
    gender: child.gender || '',
    program: child.program || '',
    groupId: child.groupId || '',
    allergy: cleanAllergy(child.allergyOverride || child.displayAllergies || ''),
    parentName: child.parentName || '',
    parentContact: child.parentContact || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!String(form.firstName ?? '').trim()) { showToast('First name is required.'); return; }
    setSaving(true);
    try {
      // Lock every registration-owned field the user actually changed.
      const previouslyLocked = String(child.manualFields || '').split(',').map(s => s.trim()).filter(Boolean);
      const lockedNow = new Set(previouslyLocked);
      EDITABLE_REG_FIELDS.forEach(f => {
        if (String(form[f] ?? '') !== String(child[f] ?? '')) lockedNow.add(f);
      });

      // String() everything before .trim(): synced kids arrive with NUMERIC
      // grade and parentContact from the sheet, and (5).trim() throws — which
      // made every save of a registered kid fail.
      const update = {
        id: child.id,
        firstName: String(form.firstName ?? '').trim(),
        lastName: String(form.lastName ?? '').trim(),
        grade: String(form.grade ?? '').trim(),
        gender: form.gender,
        program: form.program,
        groupId: form.groupId,
        parentName: String(form.parentName ?? '').trim(),
        parentContact: String(form.parentContact ?? '').trim(),
        manualFields: Array.from(lockedNow).join(','),
      };
      if (form.groupId !== (child.groupId || '')) update.groupManual = 'true';
      // Allergy: the staff value lives in allergyOverride and always wins the
      // display; registration keeps syncing underneath for reference.
      //   typed == registration → no override needed
      //   typed empty but registration has one → override "None" (staff says no allergy)
      //   otherwise → override = the typed correction
      const regAllergy = cleanAllergy(child.allergyTags || child.allergyRaw || '');
      const newAllergy = String(form.allergy ?? '').trim();
      if (newAllergy === regAllergy) update.allergyOverride = '';
      else if (newAllergy === '') update.allergyOverride = 'None';
      else update.allergyOverride = newAllergy;

      await upsertKidsChild(update);
      onSaved({ ...update, displayAllergies: update.allergyOverride ? update.allergyOverride : regAllergy });
    } catch (e) {
      // Name the actual problem — a generic toast hid a client-side TypeError
      // for a week.
      showToast(`Save failed — ${e.message || 'try again'}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Edit Kid — {child.firstName} {child.lastName}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
          </div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">Grade (1-6, blank for littles)</label><input className="form-input" value={form.grade} onChange={e => set('grade', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Gender</label>
              <BrandSelect value={form.gender} options={['', 'Girl', 'Boy']} labels={{ '': '—' }} onChange={v => set('gender', v)} width="100%" />
            </div>
          </div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">Program</label>
              <BrandSelect value={form.program} options={PROGRAMS} onChange={v => set('program', v)} width="100%" />
            </div>
            <div className="form-group"><label className="form-label">Group</label>
              <BrandSelect value={form.groupId} options={['', ...groups.filter(g => !form.program || !g.program || g.program === form.program).map(g => g.id)]} labels={{ '': '— no group —', ...Object.fromEntries(groups.map(g => [g.id, g.name])) }} onChange={v => set('groupId', v)} width="100%" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Allergies</label>
            <input className="form-input" value={form.allergy} onChange={e => set('allergy', e.target.value)} placeholder="Blank = no allergy" />
            {cleanAllergy(child.allergyRaw) && (
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 5 }}>
                Registration says: {child.allergyRaw}
              </div>
            )}
          </div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">Parent / Guardian</label><input className="form-input" value={form.parentName} onChange={e => set('parentName', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Parent Contact</label><input className="form-input" value={form.parentContact} onChange={e => set('parentContact', e.target.value)} /></div>
          </div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', lineHeight: 1.5 }}>
            Anything you change here is locked against Brushfire syncs — the app becomes the source of
            truth for that field. New info from registration still flows in for untouched fields.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// Day-of walk-up add (they register in Brushfire for the nametag; this puts
// them straight into a group here without waiting on the next sync).
function AddKidModal({ groups, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', grade: '', gender: '', program: 'Elementary', groupId: '', allergyRawInput: '', parentName: '', parentContact: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!String(form.firstName ?? '').trim()) { showToast('First name is required.'); return; }
    setSaving(true);
    try {
      const saved = await upsertKidsChild({ ...form, source: 'Manual', groupManual: form.groupId ? 'true' : '' });
      onSaved(saved);
    } catch (e) {
      showToast('Save failed — try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Add Kid (day-of)</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} autoFocus /></div>
            <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
          </div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">Grade (1-6, blank for littles)</label><input className="form-input" value={form.grade} onChange={e => set('grade', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Gender</label>
              <BrandSelect value={form.gender} options={['', 'Girl', 'Boy']} labels={{ '': '—' }} onChange={v => set('gender', v)} width="100%" />
            </div>
          </div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">Program</label>
              <BrandSelect value={form.program} options={PROGRAMS} onChange={v => set('program', v)} width="100%" />
            </div>
            <div className="form-group"><label className="form-label">Group</label>
              <BrandSelect value={form.groupId} options={['', ...groups.filter(g => !g.program || g.program === form.program).map(g => g.id)]} labels={{ '': '— pick later —', ...Object.fromEntries(groups.map(g => [g.id, g.name])) }} onChange={v => set('groupId', v)} width="100%" />
            </div>
          </div>
          <div className="form-group"><label className="form-label">Allergies (as the parent wrote them)</label><input className="form-input" placeholder="e.g. gluten free, strawberries" value={form.allergyRawInput} onChange={e => set('allergyRawInput', e.target.value)} /></div>
          <div className="two-col" style={{ marginBottom: 0 }}>
            <div className="form-group"><label className="form-label">Parent / Guardian</label><input className="form-input" value={form.parentName} onChange={e => set('parentName', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Parent Contact</label><input className="form-input" value={form.parentContact} onChange={e => set('parentContact', e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add Kid'}</button>
        </div>
      </div>
    </div>
  );
}

// Group management: rename, recolor (preset grid + custom), add, remove.
function ManageGroupsModal({ groups, onClose, onChanged, showToast }) {
  const [list, setList] = useState(groups);
  const [newName, setNewName] = useState('');
  const [newProgram, setNewProgram] = useState('Elementary');
  const [newColor, setNewColor] = useState('#6AA292');

  async function rename(g, name) {
    if (!name.trim() || name === g.name) return;
    const saved = await upsertKidsGroup({ ...g, name: name.trim() }).catch(() => null);
    if (saved) { setList(l => l.map(x => x.id === g.id ? saved : x)); onChanged(); }
  }
  async function recolor(g, color) {
    const saved = await upsertKidsGroup({ ...g, color }).catch(() => null);
    if (saved) { setList(l => l.map(x => x.id === g.id ? saved : x)); onChanged(); }
  }
  async function add() {
    if (!newName.trim()) return;
    const saved = await upsertKidsGroup({ name: newName.trim(), program: newProgram, color: newColor }).catch(() => null);
    if (saved) { setList(l => sortKidsGroups([...l, saved])); setNewName(''); onChanged(); showToast('Group added.'); }
  }
  async function remove(g) {
    await deleteKidsGroup(g.id).catch(() => {});
    setList(l => l.filter(x => x.id !== g.id));
    onChanged();
    showToast(`${g.name} removed — its kids are now unassigned.`);
  }
  // Move a group up/down in the display order. Writes an explicit manual order
  // (sortOrder 101, 102, …) for every group so the list stays exactly as
  // arranged — Daily Details, group cards, and prints all follow this order.
  async function move(g, dir) {
    const ordered = sortKidsGroups(list);
    const i = ordered.findIndex(x => x.id === g.id);
    const j = i + dir;
    if (j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    const renumbered = ordered.map((x, idx) => ({ ...x, sortOrder: MANUAL_ORDER_BASE + 1 + idx }));
    setList(renumbered);
    onChanged();
    for (const x of renumbered) {
      if (String(x.sortOrder) !== String(list.find(o => o.id === x.id)?.sortOrder)) {
        await upsertKidsGroup(x).catch(() => {});
      }
    }
    onChanged();
  }

  return (
    <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Manage Groups</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {sortKidsGroups(list).map((g, i, arr) => (
            <GroupRow
              key={g.id} group={g}
              onRename={n => rename(g, n)} onRecolor={c => recolor(g, c)} onRemove={() => remove(g)}
              onMoveUp={i > 0 ? () => move(g, -1) : null}
              onMoveDown={i < arr.length - 1 ? () => move(g, 1) : null}
            />
          ))}
          {list.length === 0 && <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>No groups yet.</div>}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <ColorSwatch color={newColor} onChange={setNewColor} />
            <input className="form-input" style={{ flex: 1, minWidth: 140, fontSize: 13 }} placeholder="New group name…" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} />
            <BrandSelect value={newProgram} options={PROGRAMS} onChange={setNewProgram} size="sm" />
            <button className="btn btn-sm btn-dark" onClick={add} disabled={!newName.trim()}>Add</button>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-dark" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function GroupRow({ group, onRename, onRecolor, onRemove, onMoveUp, onMoveDown }) {
  const [v, setV] = useState(group.name);
  useEffect(() => setV(group.name), [group.name]);
  const arrowStyle = (enabled) => ({ border: 'none', background: 'none', cursor: enabled ? 'pointer' : 'default', color: enabled ? 'var(--mid)' : 'var(--border)', padding: 1, lineHeight: 0 });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <button style={arrowStyle(!!onMoveUp)} disabled={!onMoveUp} onClick={() => onMoveUp && onMoveUp()} title="Move up">
          <svg width="10" height="7" viewBox="0 0 10 7" fill="none"><path d="M1.5 5.5L5 2l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button style={arrowStyle(!!onMoveDown)} disabled={!onMoveDown} onClick={() => onMoveDown && onMoveDown()} title="Move down">
          <svg width="10" height="7" viewBox="0 0 10 7" fill="none"><path d="M1.5 1.5L5 5l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
      <ColorSwatch color={group.color} onChange={onRecolor} />
      <input className="form-input" value={v} onChange={e => setV(e.target.value)} onBlur={() => onRename(v)} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={{ flex: 1, fontSize: 13 }} />
      <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', width: 76 }}>{group.program || '—'}</span>
      <button className="btn-remove" title="Remove group" onClick={onRemove}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}
